/**
 * Budget categories — PRD 5.7. The tracker reads this table; nothing about a
 * category (its name, its cap, its color, its column position) exists in code.
 *
 * Caps are typed in dollars and stored in integer cents. Deactivating is a soft
 * delete, so a transaction from March still knows it was Groceries.
 */
import { useCallback, useEffect, useState } from 'react';
import type { BudgetCategory, BudgetCategoryInput } from '../../shared/types';
import { ApiRequestError, api, money, parseMoney } from '../../lib/api';
import { ConfigTable } from './ConfigTable';
import { ColorField, isHex } from './ColorField';
import { AlertNote, Button, Card, Dialog, Field, Pill, TextInput } from './ui';

const DEFAULT_COLOR = '#2f6f4e';

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message || fallback;
  return fallback;
}

export function CategoryPanel({ onStatus }: { onStatus: (message: string) => void }) {
  const [items, setItems] = useState<BudgetCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A rejected action is shown where the action was taken. `actionError` belongs
  // to the panel (reorder, restore); the dialog errors are rendered inside their
  // own dialog, because a message behind an aria-modal is a message nobody sees.
  const [actionError, setActionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<BudgetCategory | 'new' | null>(null);
  const [confirming, setConfirming] = useState<BudgetCategory | null>(null);
  const [busy, setBusy] = useState(false);

  function openEditor(category: BudgetCategory | 'new') {
    setFormError(null);
    setEditing(category);
  }

  function closeEditor() {
    setFormError(null);
    setEditing(null);
  }

  function openConfirm(category: BudgetCategory) {
    setConfirmError(null);
    setConfirming(category);
  }

  function closeConfirm() {
    setConfirmError(null);
    setConfirming(null);
  }

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.get<BudgetCategory[]>(
        `/categories${showInactive ? '?include_inactive=1' : ''}`
      );
      setItems(rows);
    } catch (err) {
      setItems(null);
      setError(errorText(err, "Couldn't load your categories."));
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Optimistic: the row moves now, D1 catches up. A failure snaps it back. */
  async function reorder(ids: number[]) {
    if (!items) return;
    const byId = new Map(items.map((c) => [c.id, c]));
    const moved = ids.map((id) => byId.get(id)).filter((c): c is BudgetCategory => Boolean(c));
    const untouched = items.filter((c) => !ids.includes(c.id));
    setItems([...moved, ...untouched]);
    setActionError(null);

    try {
      await api.put<BudgetCategory[]>('/categories/reorder', { ids });
      onStatus('Column order saved.');
      await load();
    } catch (err) {
      setActionError(errorText(err, "Couldn't save the new order."));
      await load();
    }
  }

  /** The server is the authority: a rejection keeps the form open, with the reason in it. */
  async function save(input: BudgetCategoryInput) {
    setBusy(true);
    setFormError(null);
    try {
      if (editing && editing !== 'new') {
        await api.put<BudgetCategory>(`/categories/${editing.id}`, input);
        onStatus(`Saved ${input.name}.`);
      } else {
        await api.post<BudgetCategory>('/categories', input);
        onStatus(`Added ${input.name}.`);
      }
      closeEditor();
      await load();
    } catch (err) {
      setFormError(errorText(err, "Couldn't save that category."));
    } finally {
      setBusy(false);
    }
  }

  /** Restore comes from a row (panel error); turn-off comes from the confirm dialog (dialog error). */
  async function setActive(category: BudgetCategory, active: boolean) {
    setBusy(true);
    if (active) setActionError(null);
    else setConfirmError(null);

    try {
      if (active) {
        await api.put<BudgetCategory>(`/categories/${category.id}`, { active: true });
        onStatus(`${category.name} is back in the tracker.`);
      } else {
        await api.del<BudgetCategory>(`/categories/${category.id}`);
        onStatus(`${category.name} turned off. Past transactions keep it.`);
        closeConfirm();
      }
      await load();
    } catch (err) {
      const message = errorText(err, "Couldn't update that category.");
      if (active) setActionError(message);
      else setConfirmError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="categories-heading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="categories-heading" className="font-display text-xl font-semibold text-ink">
            Budget categories
          </h2>
          <p className="mt-1 max-w-prose text-sm text-ink-soft">
            Caps, colors and the order they appear in the tracker. Changes apply the next time
            anyone opens Money — no deploy.
          </p>
        </div>
        <Button variant="primary" onClick={() => openEditor('new')}>
          <span aria-hidden="true">＋</span> Add category
        </Button>
      </div>

      {actionError && <AlertNote message={actionError} />}

      <ConfigTable<BudgetCategory>
        items={items}
        error={error}
        onRetry={() => void load()}
        keyOf={(c) => c.id}
        labelOf={(c) => c.name}
        isInactive={(c) => !c.active}
        onReorder={(ids) => void reorder(ids)}
        empty={{
          title: 'No categories yet',
          body: 'Categories are how the tracker groups spending — Groceries, Dining, Amazon. Add your first one and set its monthly cap.',
          actionLabel: 'Add your first category',
          onAction: () => openEditor('new'),
        }}
        renderRow={(category) => (
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="h-8 w-8 shrink-0 rounded-full border border-border"
              style={{ backgroundColor: category.color }}
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium text-ink">{category.name}</span>
                {!category.active && <Pill>Turned off</Pill>}
              </div>
              <p className="text-sm text-ink-soft">
                {money(category.monthly_cap)}
                <span className="text-ink-faint"> / month</span>
              </p>
            </div>
          </div>
        )}
        renderActions={(category) => (
          <>
            <Button variant="ghost" onClick={() => openEditor(category)}>
              Edit
              <span className="sr-only"> {category.name}</span>
            </Button>
            {category.active ? (
              <Button variant="ghost" onClick={() => openConfirm(category)}>
                Turn off
                <span className="sr-only"> {category.name}</span>
              </Button>
            ) : (
              <Button variant="secondary" disabled={busy} onClick={() => void setActive(category, true)}>
                Restore
                <span className="sr-only"> {category.name}</span>
              </Button>
            )}
          </>
        )}
      />

      <label className="flex min-h-11 w-fit items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="h-5 w-5 rounded border-border accent-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
        />
        Show turned-off categories
      </label>

      {editing && (
        <CategoryDialog
          category={editing === 'new' ? null : editing}
          busy={busy}
          error={formError}
          onCancel={closeEditor}
          onSave={(input) => void save(input)}
        />
      )}

      {confirming && (
        <Dialog
          title={`Turn off ${confirming.name}?`}
          description="It disappears from the tracker and from new transactions. Nothing in your history changes — old transactions keep this category, and you can restore it any time."
          onClose={closeConfirm}
        >
          <div className="flex flex-col gap-4">
            {confirmError && <AlertNote message={confirmError} focusOnShow />}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={closeConfirm}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => void setActive(confirming, false)}
              >
                {busy ? 'Turning off…' : 'Turn it off'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/** A cap is only valid if it's a real, non-negative number of cents. */
function validCents(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value >= 0;
}

function CategoryDialog({
  category,
  busy,
  error,
  onCancel,
  onSave,
}: {
  category: BudgetCategory | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: BudgetCategoryInput) => void;
}) {
  const [name, setName] = useState(category?.name ?? '');
  // Dollars in the field, cents on the wire.
  const [cap, setCap] = useState(category ? (category.monthly_cap / 100).toString() : '');
  const [color, setColor] = useState(category?.color ?? DEFAULT_COLOR);
  const [errors, setErrors] = useState<{ name?: string; cap?: string; color?: string }>({});

  function submit() {
    const cents = parseMoney(cap);
    const next: typeof errors = {};
    if (!name.trim()) next.name = 'Give the category a name.';
    // parseMoney('.') is NaN, not null — a bare `cents < 0` check waves it through
    // and posts monthly_cap: NaN, which JSON turns into null.
    if (!validCents(cents)) next.cap = 'Enter a monthly cap in dollars, like 2000.';
    if (!isHex(color)) next.color = 'Pick a swatch or enter a hex like #2f6f4e.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !validCents(cents)) return;

    onSave({ name: name.trim(), monthly_cap: cents, color: color.trim().toLowerCase() });
  }

  const cents = parseMoney(cap);

  return (
    <Dialog
      title={category ? `Edit ${category.name}` : 'Add a category'}
      description="The cap is what the tracker measures the month against."
      onClose={onCancel}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* The server's reason for saying no, rendered where the person is looking. */}
        {error && <AlertNote message={error} focusOnShow />}

        <Field label="Name" error={errors.name}>
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Groceries"
              maxLength={40}
              required
            />
          )}
        </Field>

        <Field
          label="Monthly cap"
          hint="In dollars. Type 2000 for a $2,000 cap."
          error={errors.cap}
        >
          {(props) => (
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="text-ink-soft">
                $
              </span>
              <TextInput
                {...props}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                inputMode="decimal"
                placeholder="2000"
                required
              />
            </div>
          )}
        </Field>

        {validCents(cents) && (
          <Card className="bg-surface-sunk px-4 py-3 text-sm text-ink-soft">
            Saved as <span className="font-medium text-ink">{money(cents)}</span> per month.
          </Card>
        )}

        <ColorField
          label="Color"
          hint="Used for this category everywhere in the tracker."
          value={color}
          onChange={setColor}
          error={errors.color}
        />

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : category ? 'Save changes' : 'Add category'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
